import {
  AppointmentValidationInputData,
  AskConfirmationOutputData,
  AppointmentValidationOutputData,
  CancelAppointmentOutputData,
  DossierAbandonFitterOutputData,
  DossierCommentsInputData,
  DossierCommentsOutputData,
  DossierAbandonOutputData,
  FitterCommentsInputData,
  GpdrInfoRedirectInputData,
  ItineraryInputData,
  RestrictionWarnInputData,
  RestrictionWarnOutputData,
  RestartDeclarationOutputData,
  RouterInfoActionTypes,
  PhoneNumberOutputData,
} from '@sc/data-access-models';
import {
  AlreadyAbandonnedDialogComponent,
  AppointmentValidationBottomSheetComponent,
  AppointmentValidationDialogComponent,
  AskConfirmationDialogComponent,
  CancelAppointmentDialogComponent,
  CompleteAppointmentFitterDialogComponent,
  DossierAbandonDialogComponent,
  DossierAbandonFitterDialogComponent,
  DossierCommentsDialogComponent,
  FitterCommentsBottomSheetComponent,
  FitterCommentsDialogComponent,
  GpdrInfoRedirectDialogComponent,
  ItineraryBottomSheetComponent,
  ItineraryDialogComponent,
  NoShowCommentDialogComponent,
  PhoneNumberDialogComponent,
  RestartDeclarationDialogComponent,
  RestrictionWarnBottomSheetComponent,
  RestrictionWarnDialogComponent,
  RouterInfoActionDialogComponent,
} from '../components';
import { PhoneNumberBottomSheetComponent } from '../components/bottom-sheets/phone-number-bottom-sheet/phone-number-bottom-sheet.component';

/**
 * NOTE - In order to add a new overlay component, you need to do the following steps :
 * - Add an overlay type string in OverlayType
 * - Create an overlay mapping of the same structure as OverlayMappingAppointment but with adapted types for each attribute
 * - Add a condition in OverlayMapping that uses your new overlay mapping if T extends the new overlay type string
 */

export type OverlayType =
  | 'appointment-validation'
  | 'ask-confirmation'
  | 'itinerary'
  | 'fitter-comments'
  | 'dossier-abandon'
  | 'dossier-abandon-fitter'
  | 'complete-appointment'
  | 'cancel-appointment'
  | 'no-show-comment'
  | 'router-infos-action'
  | 'gpdr-info-redirect'
  | 'restriction-warn'
  | 'restart-declaration'
  | 'already-abandonned'
  | 'dossier-comments'
  | 'phone-number';

interface OverlayMappingAppointment {
  input: AppointmentValidationInputData;
  output: AppointmentValidationOutputData;
  dialogComponent: AppointmentValidationDialogComponent;
  bottomSheetComponent: AppointmentValidationBottomSheetComponent;
}

interface OverlayMappingAskConfirmation {
  input: null;
  output: AskConfirmationOutputData;
  dialogComponent: AskConfirmationDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayMappingItinerary {
  input: ItineraryInputData;
  output: null;
  dialogComponent: ItineraryDialogComponent;
  bottomSheetComponent: ItineraryBottomSheetComponent;
}
interface OverlayMappingFitterComments {
  input: FitterCommentsInputData;
  output: null;
  dialogComponent: FitterCommentsDialogComponent;
  bottomSheetComponent: FitterCommentsBottomSheetComponent;
}

interface OverlayMappingDossierAbandon {
  input: null;
  output: DossierAbandonOutputData;
  dialogComponent: DossierAbandonDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayPhoneNumber {
  input: null;
  output: PhoneNumberOutputData;
  dialogComponent: PhoneNumberDialogComponent;
  bottomSheetComponent: PhoneNumberBottomSheetComponent;
}

interface OverlayMappingDossierNoShow {
  input: null;
  output: string;
  dialogComponent: NoShowCommentDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayMappingDossierAbandonFitter {
  input: null;
  output: DossierAbandonFitterOutputData;
  dialogComponent: DossierAbandonFitterDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayMappingCompleteAppointment {
  input: null;
  output: string;
  dialogComponent: CompleteAppointmentFitterDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayMappingCancelAppointment {
  input: null;
  output: CancelAppointmentOutputData;
  dialogComponent: CancelAppointmentDialogComponent;
  bottomSheetComponent: null;
}

interface OperationTypeInfo {
  input: RouterInfoActionTypes;
  output: null;
  dialogComponent: RouterInfoActionDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayMappingGpdrInfoRedirect {
  input: GpdrInfoRedirectInputData;
  output: null;
  dialogComponent: GpdrInfoRedirectDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayMappingRestrictionWarn {
  input: RestrictionWarnInputData;
  output: RestrictionWarnOutputData;
  dialogComponent: RestrictionWarnDialogComponent;
  bottomSheetComponent: RestrictionWarnBottomSheetComponent;
}

interface OverlayRestartDeclaration {
  input: null;
  output: RestartDeclarationOutputData;
  dialogComponent: RestartDeclarationDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayAlreadyAbandonned {
  input: null;
  output: null;
  dialogComponent: AlreadyAbandonnedDialogComponent;
  bottomSheetComponent: null;
}

interface OverlayDossierComments {
  input: DossierCommentsInputData;
  output: DossierCommentsOutputData;
  dialogComponent: DossierCommentsDialogComponent;
  bottomSheetComponent: null;
}

type OverlayMapping<T extends OverlayType> = T extends 'appointment-validation'
  ? OverlayMappingAppointment
  : T extends 'ask-confirmation'
  ? OverlayMappingAskConfirmation
  : T extends 'itinerary'
  ? OverlayMappingItinerary
  : T extends 'fitter-comments'
  ? OverlayMappingFitterComments
  : T extends 'dossier-abandon'
  ? OverlayMappingDossierAbandon
  : T extends 'dossier-abandon-fitter'
  ? OverlayMappingDossierAbandonFitter
  : T extends 'complete-appointment'
  ? OverlayMappingCompleteAppointment
  : T extends 'cancel-appointment'
  ? OverlayMappingCancelAppointment
  : T extends 'no-show-comment'
  ? OverlayMappingDossierNoShow
  : T extends 'router-infos-action'
  ? OperationTypeInfo
  : T extends 'gpdr-info-redirect'
  ? OverlayMappingGpdrInfoRedirect
  : T extends 'restriction-warn'
  ? OverlayMappingRestrictionWarn
  : T extends 'restart-declaration'
  ? OverlayRestartDeclaration
  : T extends 'already-abandonned'
  ? OverlayAlreadyAbandonned
  : T extends 'dossier-comments'
  ? OverlayDossierComments
  : T extends 'phone-number'
  ? OverlayPhoneNumber
  : {};

export interface OverlayParams<T extends OverlayType> {
  type: T;
  input: OverlayMapping<T>['input'];
}

export type OverlayOutputs<T extends OverlayType> = OverlayMapping<T>['output'];

export type OverlayDialogComponent<T extends OverlayType> =
  OverlayMapping<T>['dialogComponent'];
export type OverlayBottomSheetComponent<T extends OverlayType> =
  OverlayMapping<T>['bottomSheetComponent'];
